import { useEffect, useState } from 'react'
import { Config, ConfigDetailsInterface, ConfigInputValidationsText, ConfigType } from '@library/utilities/interface'
import {
    Alert,
    ArrowLeftOutlined,
    Col,
    ColorPicker,
    FormatPainterOutlined,
    Input,
    Row,
    Select,
    SettingOutlined,
    Switch,
    Tabs,
    Tooltip,
} from 'gokwik-ui-kit'
import { InfoCircleOutlined } from '@ant-design/icons'
import { InstrumentNameMapping, PAYMENT_METHODS } from '@library/utilities/constants/constants'
import { capitalizeFirstLetter, deepCopy } from '@library/utilities/helpers/helper'
import { BulletedTextArea } from '@library/form-wizard/form-wizard'
import { ConfigNameEnum, ConfigSubcategoriesEnum } from './constants'

interface Props {
    enabledMethodsConfig: Config
    configInfo: ConfigDetailsInterface
    resetCustomiseDetails: () => void
    updateConfigDetails: (config: Config, value: any) => void
    paymentPageConfig?: Config
}

const excludedConfigs = [
    'method_positions',
    'enabled_methods',
    'preferred_panel_type',
    'page_style',
    'enable_onpl',
    'enable_native_redirection',
    'enable_twid',
    'enable_cash_to_prepaid',
]
const c2pTimers = [ConfigNameEnum.CONFIRMATION_PAGE_TIMER, ConfigNameEnum.MESSAGE_DELAY, ConfigNameEnum.WHATSAPP_TIMER]
const enableInstruments = [PAYMENT_METHODS.WALLET, PAYMENT_METHODS.EMI, PAYMENT_METHODS.BNPL, PAYMENT_METHODS.PAY_IN_X]
const subCategoryMapping = {
    wallet: PAYMENT_METHODS.WALLET,
    bnpl: PAYMENT_METHODS.BNPL,
    emi: PAYMENT_METHODS.EMI,
    pay_in_x: PAYMENT_METHODS.SIMPLPAYIN3,
}

const snakeToTitleCase = (snakeStr: string): string => {
    try {
        const acronyms = ['upi', 'psp']
        return snakeStr
            .split('_')
            .map((word) => {
                if (acronyms.includes(word.toLowerCase())) {
                    return word.toUpperCase()
                }
                return capitalizeFirstLetter(word)
            })
            .join(' ')
    } catch (error) {
        return snakeStr
    }
}

export default function ConfigDetails({
    enabledMethodsConfig,
    configInfo,
    resetCustomiseDetails,
    updateConfigDetails,
    paymentPageConfig,
}: Props) {
    const [configData, setConfigData] = useState<ConfigDetailsInterface>()
    const [subCategories, setSubCategories] = useState<string[]>([])
    const [activeInstrument, setActiveInstrument] = useState<string>('')
    const [activeTab, setActiveTab] = useState<string>('')
    const [showAlert, setShowAlert] = useState<boolean>(false)

    useEffect(() => {
        if (Object.keys(configInfo).length) {
            const subCategories = Object.keys(configInfo.config)
            const filteredSubCategories = subCategories.filter(subCategory => 
                !(configInfo.config[subCategory].some(config => config.category === 'twid' && subCategory === 'general'))
            );
            const instrument = filteredSubCategories.find((subCategory) => enableInstruments.includes(subCategory)) || ''
            setActiveInstrument(instrument)
            setActiveTab(filteredSubCategories[0])
            setSubCategories(filteredSubCategories)
            setConfigData(configInfo)
        }
    }, [configInfo])

    const updateValue = (config: Config, subCategory: string, value: any) => {
        const isTimerConfig = (name: string) => c2pTimers.includes(name as ConfigNameEnum)

        const updatedData = configData?.config[subCategory].map((data) => {
            if (data.blueprint_id !== config.blueprint_id) return data

            if (isTimerConfig(data.name)) {
                const currentTotal = configData?.config[subCategory]
                    .filter((item) => c2pTimers.includes(item.name as ConfigNameEnum) && !isNaN(item.value as number))
                    .reduce((sum, item) => sum + Number(item.value), 0)
                const newTotal = currentTotal - Number(data.value) + Number(value)

                if (newTotal > 20) {
                    setShowAlert(true)
                    return data
                }
            }
            return { ...data, value }
        })
        const alertWasShown = updatedData.every((data, index) => data === configData?.config[subCategory][index])
        if (!alertWasShown) {
            setShowAlert(false)
            setConfigData({ ...configData, config: { ...configData?.config, [subCategory]: updatedData } })
            updateConfigDetails(config, value)
        }
    }

    const updateInstrument = (config: Config, value: any, instrument: string) => {
        if (Array.isArray(config.value)) {
            if (value) config.value = [...new Set([...config.value, instrument])]
            else config.value = config.value.filter((item) => item !== instrument)
        } else {
            config.value = value ? [instrument] : []
        }
        setConfigData((prev) => {
            const updatedConfigData = deepCopy(prev)
            updatedConfigData.config['instruments'] = [config]
            return updatedConfigData
        })
        updateConfigDetails(config, config.value)
    }

    const moveToConfigs = () => {
        resetCustomiseDetails()
    }

    return (
        <Row className='bg-white mt-4 p-4 rounded-md box-border flex'>
            <Col span={24} className='flex gap-4 mb-2'>
                <ArrowLeftOutlined onClick={moveToConfigs} />
                <h4 className='m-0 text-base font-semibold'>{configData?.name ?? ''}</h4>
            </Col>
            <Col span={24}>
                <Tabs
                    onChange={(key) => setActiveTab(key)}
                    activeKey={activeTab}
                    items={[
                        ...subCategories?.map((subCategory) => {
                            const configs = configData?.config[subCategory]
                            const label = configs[0]?.subcategoryTitle ?? 'General'
                            return subCategory === 'instruments'
                                ? {
                                      key: subCategory,
                                      label: 'Instruments',
                                      children: (
                                          <EnableInstruments
                                              enabledMethodsConfig={enabledMethodsConfig}
                                              updateValue={updateInstrument}
                                              subCategory={activeInstrument}
                                          />
                                      ),
                                      icon: <SettingOutlined />,
                                  }
                                : {
                                      key: subCategory,
                                      label,
                                      children: (
                                          <Settings
                                              paymentConfigs={configs}
                                              updateValue={updateValue}
                                              paymentBtnStyleConfig={paymentPageConfig}
                                              subCategory={subCategory}
                                              showAlert={showAlert}
                                          />
                                      ),
                                      icon: <FormatPainterOutlined />,
                                  }
                        }),
                    ]}
                />
            </Col>
        </Row>
    )
}

const Settings = ({ paymentConfigs, updateValue, paymentBtnStyleConfig, subCategory, showAlert }) => {
    const totalC2pTimer = paymentConfigs
        .filter((config) => c2pTimers.includes(config.name) && !isNaN(config.value))
        .reduce((sum, config) => sum + Number(config.value), 0)
    return (
        <>
            {subCategory === ConfigSubcategoriesEnum.WHATSAPP && (
                <Alert
                    className='mt-3 mb-3'
                    showIcon
                    message={
                        <p className='p-2'>
                            Explore Kwikchat for more customisations. Allows sending C2P messages via branded WhatsApp
                            handles as well as customisation in message template and addition of additional nudges on
                            WhatsApp
                        </p>
                    }
                    type='info'
                />
            )}
            {showAlert && subCategory === ConfigSubcategoriesEnum.TIMERS && (
                <Alert
                    className='mt-3 mb-3'
                    type='error'
                    showIcon
                    message={<p className='p-2'>The total C2P expiration time must not exceed 20 minutes</p>}
                />
            )}
            <Row className='p-2 flex flex-col gap-6'>
                {paymentConfigs?.map(
                    (config) =>
                        !excludedConfigs.includes(config.name) && (
                            <Col
                                span={24}
                                key={config.blueprint_id}
                                className={`w-full flex ${
                                    config.type !== ConfigType.LIST ? 'items-center' : 'flex-col'
                                } flex-start justify-between`}
                            >
                                <div className='header w-[60%]'>
                                    <h4 className='m-0 text-sm font-medium'>{config.title}</h4>
                                    {config?.description && (
                                        <p className='pt-1 text-gray-500 text-xs font-normal'>{config.description}</p>
                                    )}
                                </div>
                                <div
                                    className={`value ${
                                        config.type === ConfigType.LIST
                                            ? 'w-[100%] justify-start pt-2'
                                            : 'w-[40%] justify-end flex'
                                    } pr-1`}
                                >
                                    {config.type === ConfigType.TEXT && (
                                        <Input
                                            size='middle'
                                            placeholder={`Enter ${config.title}`}
                                            type='text'
                                            value={config.value as string}
                                            maxLength={
                                                (config?.validations as ConfigInputValidationsText)?.max_length ?? 50
                                            }
                                            minLength={
                                                (config?.validations as ConfigInputValidationsText)?.min_length ?? 0
                                            }
                                            onChange={(e) => updateValue(config, config.subcategory, e.target.value)}
                                        />
                                    )}
                                    {config.type === ConfigType.NUMERIC && (
                                        <Input
                                            size='middle'
                                            placeholder={`Enter ${config.title}`}
                                            type='number'
                                            value={config.value}
                                            max={
                                                (config?.validations as ConfigInputValidationsText)?.max_number ??
                                                Infinity
                                            }
                                            min={(config?.validations as ConfigInputValidationsText)?.min_number ?? 0}
                                            onChange={(e) => updateValue(config, config.subcategory, Number(e.target.value))}
                                        />
                                    )}
                                    {config.type === ConfigType.TOGGLE && (
                                        <>
                                            {config?.name === 'enable_ppcod' && (
                                                <Tooltip
                                                    title='Reach out to Gokwik team for updating PPCOD Status'
                                                    className='mr-2'
                                                >
                                                    <InfoCircleOutlined />
                                                </Tooltip>
                                            )}
                                            <Switch
                                                checked={(config?.value as boolean) ?? false}
                                                onChange={(data) => updateValue(config, config.subcategory, data)}
                                                disabled={config?.name === 'enable_ppcod'}
                                            />
                                        </>
                                    )}
                                    {config.type === ConfigType.COLOR && (
                                        <>
                                            {['button_bg_color', 'button_color'].includes(config?.name) &&
                                                paymentBtnStyleConfig?.value === 'vanilla' && (
                                                    <Tooltip
                                                        title='Colors cannot be customised in the list style theme'
                                                        className='mr-2'
                                                    >
                                                        <InfoCircleOutlined />
                                                    </Tooltip>
                                                )}
                                            <ColorPicker
                                                className='w-[110px]'
                                                defaultValue={config?.value ?? '#1677ff'}
                                                showText
                                                allowClear
                                                format='hex'
                                                disabledAlpha
                                                disabledFormat
                                                value={
                                                    ['button_bg_color', 'button_color'].includes(config?.name) &&
                                                    paymentBtnStyleConfig?.value === 'vanilla'
                                                        ? 'black'
                                                        : config?.value
                                                }
                                                onChange={(_, color) => updateValue(config, config.subcategory, color)}
                                                disabled={
                                                    ['button_bg_color', 'button_color'].includes(config?.name) &&
                                                    paymentBtnStyleConfig?.value === 'vanilla'
                                                }
                                            />
                                        </>
                                    )}
                                    {config.type === ConfigType.SELECT && (
                                        <Select
                                            size='middle'
                                            value={config?.value}
                                            onChange={(e) => updateValue(config, config.subcategory, e)}
                                            options={
                                                config?.validations?.options?.map((option) => ({
                                                    label: snakeToTitleCase(option),
                                                    value: option,
                                                })) ?? []
                                            }
                                        ></Select>
                                    )}
                                    {config.type === ConfigType.LIST && (
                                        <BulletedTextArea
                                            placeholder={`Enter ${config.title} (one item per line)`}
                                            value={(config.value as string[])?.join('\n') ?? ''}
                                            onChange={(e) => {
                                                const targetValue = e ?? ''
                                                const values = targetValue
                                                    .split('\n')
                                                    .map((item) => item.trim())
                                                    .filter((item) => item)
                                                updateValue(config, config.subcategory, values)
                                            }}
                                        />
                                    )}
                                </div>
                            </Col>
                        ),
                )}
                {subCategory === ConfigSubcategoriesEnum.TIMERS && (
                    <Col span={24} className='w-full flex items-center justify-between'>
                        <div className='header w-[60%]'>
                            <h4 className='m-0 text-sm font-medium'>C2P Expiration Time</h4>

                            <p className='pt-1 text-gray-500 text-xs font-normal'>
                                Total Time (in minutes) for customer to convert their COD order to prepaid
                            </p>
                        </div>
                        <div className='value w-[40%] justify-end flex pr-1'>
                            <Input size='middle' type='number' value={totalC2pTimer} disabled />
                        </div>
                    </Col>
                )}
            </Row>
        </>
    )
}

const EnableInstruments = ({ enabledMethodsConfig, updateValue, subCategory }) => {
    const mappedSubCategory = subCategoryMapping[subCategory] || []

    const filteredOptions = enabledMethodsConfig?.validations?.options?.filter((config) => {
        if (Array.isArray(mappedSubCategory)) {
            return mappedSubCategory.some((keyword) => config.includes(keyword))
        } else {
            return config.includes(mappedSubCategory)
        }
    })
    return (
        <Row className='p-2 flex flex-col gap-6'>
            {filteredOptions?.map((configName) => (
                <Col span={24} key={configName} className='w-full flex justify-between flex-start items-center'>
                    <div className='header w-[30%] '>
                        <h4 className='m-0 text-sm font-medium'>{InstrumentNameMapping[configName]}</h4>
                    </div>
                    <div className='value w-[70%] flex justify-end pr-1'>
                        <Switch
                            size='small'
                            checked={enabledMethodsConfig?.value?.includes(configName) ?? false}
                            onChange={(data) => updateValue(enabledMethodsConfig, data, configName)}
                        />
                    </div>
                </Col>
            ))}
        </Row>
    )
}
